/**
 * Conditional-access policies — Zod schemas.
 *
 * A policy is an ordered rule: "when these conditions match, apply
 * these controls". Evaluation walks the policy list in priority
 * order and stops at the first match, so ordering is load-bearing.
 *
 * The policy model intentionally mirrors the Microsoft Entra /
 * Okta conditional-access shape so operators migrating from those
 * platforms can map their rules 1:1.
 */

import { z } from "zod";

// ── Conditions ────────────────────────────────────────────────────────────

// Note: we deliberately avoid `.default([])` on array fields. In Zod
// 4 defaults widen the input type to `undefined`-allowed, which
// breaks react-hook-form's resolver generics (the input and output
// types stop matching). Callers must always provide empty arrays
// explicitly.
export const userScopeSchema = z.object({
  kind: z.literal("user"),
  include_all_users: z.boolean(),
  include_user_ids: z.array(z.string()),
  include_group_ids: z.array(z.string()),
  exclude_user_ids: z.array(z.string()),
  exclude_group_ids: z.array(z.string()),
});

export const applicationScopeSchema = z.object({
  kind: z.literal("application"),
  include_all_applications: z.boolean(),
  include_application_ids: z.array(z.string()),
  exclude_application_ids: z.array(z.string()),
});

export const locationScopeSchema = z.object({
  kind: z.literal("location"),
  include_any_location: z.boolean(),
  include_named_location_ids: z.array(z.string()),
  exclude_named_location_ids: z.array(z.string()),
});

export const deviceScopeSchema = z.object({
  kind: z.literal("device"),
  require_managed: z.boolean(),
  require_compliant: z.boolean(),
  allowed_platforms: z.array(
    z.enum(["ios", "android", "macos", "windows", "linux"]),
  ),
});

export const conditionSchema = z.discriminatedUnion("kind", [
  userScopeSchema,
  applicationScopeSchema,
  locationScopeSchema,
  deviceScopeSchema,
]);
export type PolicyCondition = z.infer<typeof conditionSchema>;

// ── Controls ──────────────────────────────────────────────────────────────

export const controlSchema = z.object({
  /** Block matching sign-ins entirely. Wins over all grant controls. */
  block: z.boolean(),
  /** Require WebAuthn step-up ceremony before allowing the action. */
  require_step_up: z.boolean(),
  /** Require a platform authenticator (exclude hybrid / security keys). */
  require_platform_authenticator: z.boolean(),
  /** Require a compliant / managed device. */
  require_managed_device: z.boolean(),
  /** Maximum session lifetime in minutes. 0 = no override. */
  session_lifetime_minutes: z.number().int().nonnegative(),
});
export type PolicyControls = z.infer<typeof controlSchema>;

// ── Policy record ─────────────────────────────────────────────────────────

export const policyStateSchema = z.enum(["enabled", "disabled", "report_only"]);
export type PolicyState = z.infer<typeof policyStateSchema>;

export const policySchema = z.object({
  id: z.string(),
  /** Optimistic-concurrency version. Increments on every server-side
   * update; send back as `expected_version` on PUT so two admins
   * editing the same policy at once get a 409 instead of silently
   * clobbering each other. */
  version: z.number().int().nonnegative(),
  name: z.string(),
  description: z.string().nullable(),
  state: policyStateSchema,
  priority: z.number().int().nonnegative(),
  conditions: z.array(conditionSchema),
  controls: controlSchema,
  created_at: z.string(),
  updated_at: z.string(),
  last_hit_at: z.string().nullable(),
  hit_count_24h: z.number().int().nonnegative(),
});
export type Policy = z.infer<typeof policySchema>;

export const policiesListResponseSchema = z.object({
  data: z.array(policySchema),
});
export type PoliciesListResponse = z.infer<typeof policiesListResponseSchema>;

// ── Mutation inputs ───────────────────────────────────────────────────────

export const createPolicySchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  state: policyStateSchema,
  conditions: z.array(conditionSchema).min(1, "At least one condition is required"),
  controls: controlSchema,
});
export type CreatePolicyInput = z.infer<typeof createPolicySchema>;

export const updatePolicySchema = createPolicySchema.partial().extend({
  /** Optimistic concurrency guard — the form threads this through
   * from the loaded `policy.version` so the backend can 409 a stale
   * editor instead of clobbering a concurrent edit. */
  expected_version: z.number().int().nonnegative(),
});
export type UpdatePolicyInput = z.infer<typeof updatePolicySchema>;

export const updatePolicyStateSchema = z.object({
  state: policyStateSchema,
});

export const reorderPoliciesSchema = z.object({
  policy_ids: z.array(z.string()).min(1),
});
export type ReorderPoliciesInput = z.infer<typeof reorderPoliciesSchema>;
